import { useState, useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Link, Navigate, useNavigate } from 'react-router-dom'
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Container,
  ThemeProvider,
  createTheme,
  Theme,
  Button,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  Tooltip,
  CircularProgress as MuiCircularProgress
} from '@mui/material'
import {
  Menu as MenuIcon,
  CloudUpload as DeployIcon,
  Description as PoliciesIcon,
  Image as AssetsIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  Security as SecurityIcon,
  SupportAgent as SupportAgentIcon,
  Lock as LockIcon,
  SwapHoriz as MigrationsIcon
} from '@mui/icons-material'
import { useMsal, useIsAuthenticated } from '@azure/msal-react'
import { loginRequest } from './authConfig'
import api, { getBranding, getAssets, Asset, createSupportTicket, getCertificateStatus } from './services/api'
//import Dashboard from './components/Dashboard'
import DeploymentWizard from './components/DeploymentWizard'
import PoliciesView from './components/PoliciesView'
import AIAssistant from './components/AIAssistant'
import AssetManager from './components/AssetManager'
import AccountSettings from './components/AccountSettings'
import MSPAccess from './components/MSPAccess'
import MarketplaceLanding from './components/MarketplaceLanding'
import AdminSetupFlow from './components/AdminSetupFlow'
import ConsentCallback from './pages/ConsentCallback'
import SupportDialog from './shared/support/SupportDialog'
import type { SupportDialogContext, SupportDialogValues } from './shared/support/SupportTypes'
import useConnectTokenVerification from './hooks/useConnectTokenVerification'
// import { FEATURES, hasFeatureClaim } from './utils/constants'  // Moved to Option A: Entra-only config

const CustomerMigrationsModule = lazy(() => import('./features/migrations/CustomerMigrationsModule'))
//
const MARKETPLACE_URL = import.meta.env.VITE_MARKETPLACE_URL
const SOFTWARE_URL = import.meta.env.VITE_SOFTWARE_URL
const drawerWidth = 240

// Standardized Toggle Styles
const switchThemeOverrides: Theme['components'] = {
  MuiSwitch: {
    styleOverrides: {
      root: {
        width: 42,
        height: 26,
        padding: 0,
        marginLeft: 8,
      },
      switchBase: {
        padding: 0,
        margin: 2,
        transitionDuration: '300ms',
        '&.Mui-checked': {
          transform: 'translateX(16px)',
          color: '#fff',
          '& + .MuiSwitch-track': {
            opacity: 1,
            border: 0,
          },
          '& .MuiSwitch-thumb::before': {
            content: "'on'",
          },
        },
      },
      thumb: {
        boxSizing: 'border-box' as const,
        width: 22,
        height: 22,
        '&::before': {
          content: "'off'",
          position: 'absolute' as const,
          width: '100%',
          height: '100%',
          left: 0,
          top: 0,
          display: 'flex' as const,
          alignItems: 'center' as const,
          justifyContent: 'center' as const,
          fontSize: '7px',
          textTransform: 'uppercase' as const,
          color: '#777',
        },
      },
      track: {
        borderRadius: 13,
        backgroundColor: '#E9E9EA',
        opacity: 1,
      },
    },
  },
};

// Default theme
const defaultTheme = createTheme({
  palette: {
    primary: {
      main: '#0078d4', // Microsoft blue
    },
    secondary: {
      main: '#50e6ff',
    },
  },
  components: switchThemeOverrides,
})

function App() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [customerData, setCustomerData] = useState<any>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [theme, setTheme] = useState(defaultTheme)
  const [customerAssets, setCustomerAssets] = useState<Asset[]>([])
  const [needsAdminSetup, setNeedsAdminSetup] = useState(false)
  const [checkingCertificateStatus, setCheckingCertificateStatus] = useState(false)
  const [hasValidCertificate, setHasValidCertificate] = useState<boolean | null>(null)
  const navigate = useNavigate()
  const [checkingSetupStatus, setCheckingSetupStatus] = useState(true)
  const [supportOpen, setSupportOpen] = useState(false)
  const [supportSubmitting, setSupportSubmitting] = useState(false)
  const [supportError, setSupportError] = useState<string | null>(null)
  const [supportSuccess, setSupportSuccess] = useState<string | null>(null)

    // Public route detection: marketplace landing must be accessible before auth (same pattern as MSP portal)
    const isMarketplaceLandingRoute = window.location.pathname.toLowerCase().startsWith('/marketplace/landing')
    // Consent callback must be accessible before auth — Azure redirects here with admin_consent params
    const isConsentCallbackRoute = window.location.pathname.toLowerCase() === '/consent-callback'

    const [portalAccess, setPortalAccess] = useState({
    canAccessPortal: false,
    canAccessFullPortal: false,
    canDeployPolicies: false,
    canCreateGroups: false,
    canDeployBrandingOnly: false,
    requiresEnterpriseClientLicense: false,
    hasValidEnterpriseClientLicense: true,
    portalLockReason: null as string | null,
    isLifecycleLocked: false,
    lifecycleState: 'Active' as string,
    dataRetentionUntil: null as string | null,
  })
  
  const { instance, accounts } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const isDevelopment = import.meta.env.DEV
  // T2T_MIGRATION gating moved to Option A: configured in Entra, enforced by backend middleware
  const { verified: connectTokenVerified, params: connectTokenParams } = useConnectTokenVerification()

  const intendedTenantId = (() => {
    const searchParams = new URLSearchParams(window.location.search)
    const tenant = searchParams.get('tenant')?.trim()
    return tenant && tenant.length > 0 ? tenant : null
  })()

  const tenantReauthKey = intendedTenantId
    ? `TCConfigCustomerPortalTenantReauth:${intendedTenantId.toLowerCase()}`
    : null

  const activeCustomerTenantId = (customerData?.tenantId || intendedTenantId || '').toLowerCase()
  const verifiedMspTenantId =
    connectTokenVerified &&
    connectTokenParams?.mspTenantId &&
    connectTokenParams?.customerTenantId?.toLowerCase() === activeCustomerTenantId
      ? connectTokenParams.mspTenantId
      : undefined

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleLogin = () => {
    const request: any = { ...loginRequest }
    if (intendedTenantId) {
      request.authority = `https://login.microsoftonline.com/${intendedTenantId}`
      request.prompt = 'select_account'
    }

    instance.loginRedirect(request).catch(e => {
      console.error(e)
    })
  }

  const handleLogout = () => {
    handleMenuClose()
    instance.logoutRedirect().catch(e => {
      console.error(e)
    })
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleOpenSupport = () => {
    setSupportError(null)
    setSupportSuccess(null)
    setSupportOpen(true)
  }

  const handleCloseSupport = () => {
    if (supportSubmitting) {
      return
    }

    setSupportOpen(false)
    setSupportError(null)
    setSupportSuccess(null)
  }

  // Helper function to generate AdminSetup hash (matches backend logic)
  const generateAdminSetupHash = async (tenantId: string): Promise<string> => {
    const input = `AdminSetup:${tenantId}`
    const encoder = new TextEncoder()
    const data = encoder.encode(input)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashBase64 = btoa(String.fromCharCode.apply(null, hashArray as any))
    return hashBase64
  }

  useEffect(() => {
    // Load customer data when authenticated
    console.log('App.tsx: isAuthenticated =', isAuthenticated);
    setHasValidCertificate(null); // Reset on auth/tenant change
    // Marketplace onboarding route owns its own initialization lifecycle.
    // Avoid dashboard bootstrap API calls while onboarding is in progress.
    if (isMarketplaceLandingRoute) {
      setCheckingSetupStatus(false)
      return
    }

    if (isAuthenticated && accounts.length > 0) {
      const normalizedIntendedTenantId = intendedTenantId?.toLowerCase() ?? null

      const uniqueAccounts = (() => {
        const seen = new Set<string>()
        const ordered = [instance.getActiveAccount(), ...accounts].filter(Boolean) as typeof accounts
        return ordered.filter(acc => {
          const key = `${acc.homeAccountId}:${(acc.tenantId ?? '').toLowerCase()}`
          if (seen.has(key)) {
            return false
          }

          seen.add(key)
          return true
        })
      })()

      // If tenant is explicitly requested, use that account. Otherwise probe accounts and select the first tenant
      // that actually has a customer subscription.
      const resolveTenantContext = async () => {
        if (normalizedIntendedTenantId) {
          const explicitTenantAccount = uniqueAccounts.find(a => (a.tenantId ?? '').toLowerCase() === normalizedIntendedTenantId)
          if (!explicitTenantAccount) {
            return { account: null as (typeof accounts[number] | null), tenantId: intendedTenantId!, subscription: null as any }
          }

          const sub = await api.get(`/tenants/${explicitTenantAccount.tenantId}/subscription`)
          return { account: explicitTenantAccount, tenantId: explicitTenantAccount.tenantId, subscription: sub.data }
        }

        for (const candidate of uniqueAccounts) {
          if (!candidate.tenantId) {
            continue
          }

          try {
            const sub = await api.get(`/tenants/${candidate.tenantId}/subscription`)
            return { account: candidate, tenantId: candidate.tenantId, subscription: sub.data }
          } catch {
            // Continue probing other cached tenant contexts.
          }
        }

        const fallback = uniqueAccounts[0] ?? null
        return { account: fallback, tenantId: fallback?.tenantId ?? '', subscription: null as any }
      }

      // Fetch actual company data from API and check for AdminSetup requirement
      const fetchCompanyData = async () => {
        let resolvedTenantId = ''
        let resolvedUsername = ''

        try {
          const resolved = await resolveTenantContext()
          const account = resolved.account
          const tenantId = resolved.tenantId

          resolvedTenantId = tenantId
          resolvedUsername = account?.username ?? ''

          if (!account || !tenantId) {
            throw new Error('Could not resolve a valid tenant account for this portal session.')
          }

          if (normalizedIntendedTenantId && tenantId.toLowerCase() !== normalizedIntendedTenantId) {
            const alreadyRetried = tenantReauthKey ? sessionStorage.getItem(tenantReauthKey) === '1' : false
            if (!alreadyRetried) {
              if (tenantReauthKey) {
                sessionStorage.setItem(tenantReauthKey, '1')
              }

              const tenantRequest: any = {
                ...loginRequest,
                authority: `https://login.microsoftonline.com/${intendedTenantId}`,
                prompt: 'select_account',
              }

              console.warn('App.tsx: Re-authenticating to intended tenant', {
                intendedTenantId,
                activeTenantId: tenantId,
              })

              instance.loginRedirect(tenantRequest).catch(e => {
                console.error('App.tsx: Tenant-targeted re-auth failed', e)
                setCheckingSetupStatus(false)
              })

              return
            }
          }

          if (tenantReauthKey) {
            sessionStorage.removeItem(tenantReauthKey)
          }

          if (instance.getActiveAccount()?.homeAccountId !== account.homeAccountId) {
            instance.setActiveAccount(account)
          }

          const subscription = resolved.subscription ?? (await api.get(`/tenants/${tenantId}/subscription`)).data;
          
          // Check if customer needs to complete admin setup (certificate wizard)
          const marketplaceStatus = subscription.MarketplaceSubscriptionStatus;
          console.log('App.tsx: MarketplaceSubscriptionStatus:', marketplaceStatus);
          
          if (marketplaceStatus) {
            // Generate expected hash and compare
            const expectedHash = await generateAdminSetupHash(tenantId);
            console.log('App.tsx: Expected AdminSetup hash:', expectedHash);
            console.log('App.tsx: Actual status:', marketplaceStatus);
            
            if (marketplaceStatus === expectedHash) {
              console.log('App.tsx: Customer needs to complete admin setup (certificate wizard)');
              setNeedsAdminSetup(true);
            } else {
              console.log('App.tsx: Customer has completed setup or marketplace flow');
              setNeedsAdminSetup(false);
            }
          } else {
            console.log('App.tsx: No MarketplaceSubscriptionStatus - assuming legacy or marketplace customer');
            setNeedsAdminSetup(false);
          }
          
          const authData = {
            tenantId: tenantId,
            companyName: subscription.CompanyName || account.name || 'Loading...',
            subscriptionTier: subscription.SubscriptionTier || 'Standard',
            userEmail: account.username,
            marketplacePlanId: subscription.MarketplacePlanId || null
          };
          setPortalAccess({
            canAccessPortal: subscription.CanAccessPortal ?? false,
            canAccessFullPortal: subscription.CanAccessFullPortal ?? false,
            canDeployPolicies: subscription.CanDeployPolicies ?? false,
            canCreateGroups: subscription.CanCreateGroups ?? false,
            canDeployBrandingOnly: subscription.CanDeployBrandingOnly ?? false,
            requiresEnterpriseClientLicense: subscription.RequiresEnterpriseClientLicense ?? false,
            hasValidEnterpriseClientLicense: subscription.HasValidEnterpriseClientLicense ?? true,
            portalLockReason: subscription.PortalLockReason ?? null,
            isLifecycleLocked: subscription.IsLifecycleLocked ?? false,
            lifecycleState: subscription.LifecycleState ?? 'Active',
            dataRetentionUntil: subscription.DataRetentionUntil ?? null,
          });
          console.log('App.tsx: Setting authenticated customer data from API:', authData);
          setCustomerData(authData);
        } catch (error) {
          console.error('App.tsx: Failed to fetch subscription data, using fallback:', error);
          // Fallback to basic auth data if API fails
          const authData = {
            tenantId: resolvedTenantId,
            companyName: 'Loading...',
            subscriptionTier: 'Standard',
            userEmail: resolvedUsername,
            marketplacePlanId: null
          };
          setCustomerData(authData);
          setPortalAccess({
            canAccessPortal: false,
            canAccessFullPortal: false,
            canDeployPolicies: false,
            canCreateGroups: false,
            canDeployBrandingOnly: false,
            requiresEnterpriseClientLicense: false,
            hasValidEnterpriseClientLicense: false,
            portalLockReason: 'Unable to verify deployment access. Please refresh and try again.',
            isLifecycleLocked: false,
            lifecycleState: 'Active',
            dataRetentionUntil: null,
          });
          setNeedsAdminSetup(false);
        } finally {
          setCheckingSetupStatus(false);
        }
      };

      fetchCompanyData();
    }
  }, [isAuthenticated, accounts, intendedTenantId, isMarketplaceLandingRoute])

  // When customerData or needsAdminSetup changes, check certificate status if needed
  useEffect(() => {
    if (
      customerData?.tenantId &&
      !needsAdminSetup &&
      isAuthenticated &&
      hasValidCertificate === null &&
      !checkingCertificateStatus
    ) {
      setCheckingCertificateStatus(true);
      getCertificateStatus(customerData.tenantId)
        .then(status => setHasValidCertificate(status.hasValidCertificate))
        .catch(() => setHasValidCertificate(false))
        .finally(() => setCheckingCertificateStatus(false));
    }
  }, [customerData, needsAdminSetup, isAuthenticated]);

  // Redirect to certificate wizard if no valid certificate
  useEffect(() => {
    if (
      isAuthenticated &&
      customerData?.tenantId &&
      !needsAdminSetup &&
      hasValidCertificate === false &&
      window.location.pathname !== '/certificate-wizard'
    ) {
      navigate('/certificate-wizard', { replace: true })
    }
  }, [isAuthenticated, customerData, needsAdminSetup, hasValidCertificate, navigate])

  const isPortalGated = !portalAccess.canAccessPortal
  const isEnterprisePortalLocked = portalAccess.requiresEnterpriseClientLicense && !portalAccess.canAccessFullPortal
  const isLifecycleLocked = portalAccess.isLifecycleLocked === true
  const marketplacePlanId = String(customerData?.marketplacePlanId || '').toLowerCase()
  const isSupportExcludedPlan = marketplacePlanId === 'msp-client' || marketplacePlanId === 'msp-client-dev'
  const supportContext: SupportDialogContext = {
    sourceApp: 'Customer Portal',
    portalType: 'Customer',
    tenantId: customerData?.tenantId || '',
    userName: accounts[0]?.name || customerData?.companyName || 'Customer User',
    userEmail: customerData?.userEmail || accounts[0]?.username || '',
    userRole: 'Customer',
    subscriptionTier: customerData?.subscriptionTier,
    marketplacePlanId: customerData?.marketplacePlanId || undefined,
    pageUrl: window.location.href,
  }

  const renderCertificateWizardRoute = () => {
    if (!customerData?.tenantId || checkingCertificateStatus || hasValidCertificate === null) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
            gap: 2,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Checking certificate status...
          </Typography>
        </Box>
      )
    }

    if (hasValidCertificate) {
      return <Navigate to="/account#certificate-management" replace />
    }

    return (
      <AdminSetupFlow
        tenantId={customerData.tenantId}
        tenantDomain={customerData.userEmail?.split('@')[1] || 'contoso.com'}
      />
    )
  }

  const buildSupportMailTo = (values: SupportDialogValues) => {
    const subject = values.subject.trim() || `Customer Portal Support - ${values.category}`
    const body = [
      `Source App: ${supportContext.sourceApp}`,
      `Portal Type: ${supportContext.portalType}`,
      `Tenant ID: ${supportContext.tenantId || 'N/A'}`,
      `User Name: ${supportContext.userName || 'N/A'}`,
      `User Email: ${supportContext.userEmail || 'N/A'}`,
      `Role: ${supportContext.userRole || 'N/A'}`,
      `Subscription Tier: ${supportContext.subscriptionTier || 'N/A'}`,
      `Marketplace Plan Id: ${supportContext.marketplacePlanId || 'N/A'}`,
      `Category: ${values.category}`,
      `Severity: ${values.severity}`,
      `Page URL: ${supportContext.pageUrl}`,
      '',
      values.description.trim(),
    ].join('\n')

    return `mailto:Support@tc-config.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }

  const handleEmailSupport = (values: SupportDialogValues) => {
    window.location.href = buildSupportMailTo(values)
  }

  const handleSubmitSupport = async (values: SupportDialogValues) => {
    setSupportSubmitting(true)
    setSupportError(null)
    setSupportSuccess(null)

    try {
      const result = await createSupportTicket({
        ...supportContext,
        category: values.category,
        severity: values.severity,
        subject: values.subject,
        description: values.description,
      })

      setSupportSuccess(
        result.workItemId
          ? `${result.message} Ticket ${result.ticketId} was linked to Azure DevOps work item ${result.workItemId}.`
          : `${result.message} Ticket ${result.ticketId} was created.`
      )
    } catch (error: any) {
      setSupportError(error?.message || 'Failed to submit support ticket.')
    } finally {
      setSupportSubmitting(false)
    }
  }

  // Load customer branding and apply theme
  useEffect(() => {
    const loadBranding = async () => {
      if (customerData?.tenantId) {
        try {
          const brandingData = await getBranding(customerData.tenantId)
          console.log('🎨 App.tsx - Retrieved branding data:', brandingData)
          
          // Create custom theme from branding
          if (brandingData.HeaderBackgroundColor || brandingData.BackgroundColor) {
            const headerColor = brandingData.HeaderBackgroundColor || '#0078d4'
            console.log('🎨 App.tsx - Setting headerBackgroundColor to:', headerColor)
            const customTheme = createTheme({
              palette: {
                primary: {
                  main: headerColor,
                },
                secondary: {
                  main: '#50e6ff',
                },
                background: {
                  default: brandingData.BackgroundColor || '#fafafa',
                  paper: '#ffffff',
                },
              },
              components: switchThemeOverrides,
            })
            // Store header color for access in other components
            localStorage.setItem('headerBackgroundColor', headerColor)
            console.log('🎨 App.tsx - Stored in localStorage, current value:', localStorage.getItem('headerBackgroundColor'))
            setTheme(customTheme)
          }
        } catch (error) {
          console.error('Failed to load branding:', error)
          // Use default theme if branding fails to load
        }
      }
    }

    loadBranding()
  }, [customerData])

  // Load customer assets (logos, etc.)
  useEffect(() => {
    const loadAssets = async () => {
      if (customerData?.tenantId) {
        try {
          console.log('App.tsx: Loading customer assets for tenant', customerData.tenantId)
          const assets = await getAssets(customerData.tenantId)
          console.log('App.tsx: Loaded assets:', assets)
          setCustomerAssets(assets)
        } catch (error) {
          console.error('Failed to load customer assets:', error)
        }
      }
    }

    loadAssets()
  }, [customerData])

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          TC Configuration
        </Typography>
      </Toolbar>
      <List>
        {isEnterprisePortalLocked ? null : (
          <>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/policies" disabled={isPortalGated}>
                <ListItemIcon>
                  <PoliciesIcon />
                </ListItemIcon>
                <ListItemText primary="My Policies" />
              </ListItemButton>
            </ListItem>
            {isDevelopment && (
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/migrations" disabled={isPortalGated}>
                  <ListItemIcon>
                    <MigrationsIcon />
                  </ListItemIcon>
                  <ListItemText primary="Migration" />
                </ListItemButton>
              </ListItem>
            )}
          </>
        )}
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/deploy" disabled={isPortalGated}>
            <ListItemIcon>
              <DeployIcon />
            </ListItemIcon>
            <ListItemText primary="Deploy Config" />
          </ListItemButton>
        </ListItem>
        {/* AI Assistant menu item hidden until functionality is complete */}
        {/* 
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/ai-assistant">
            <ListItemIcon>
              <AIIcon />
            </ListItemIcon>
            <ListItemText primary="AI Assistant" />
          </ListItemButton>
        </ListItem>
        */}
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/assets" disabled={isPortalGated}>
            <ListItemIcon>
              <AssetsIcon />
            </ListItemIcon>
            <ListItemText primary="Branding" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/msp-access" disabled={isPortalGated}>
            <ListItemIcon>
              <SecurityIcon />
            </ListItemIcon>
            <ListItemText primary="MSP Access" />
          </ListItemButton>
        </ListItem>
        {isEnterprisePortalLocked ? null : (
          <ListItem disablePadding>
            <ListItemButton component={Link} to="/account" disabled={isPortalGated}>
              <ListItemIcon>
                <AccountIcon />
              </ListItemIcon>
              <ListItemText primary="Account" />
            </ListItemButton>
          </ListItem>
        )}
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/certificate-wizard" disabled={isPortalGated}>
            <ListItemIcon>
              <SecurityIcon />
            </ListItemIcon>
            <ListItemText primary="Certificate Wizard" />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  )

  return (
    <ThemeProvider theme={theme}>
      {/* Public marketplace landing route — rendered before auth checks, no nav chrome */}
      {isMarketplaceLandingRoute ? (
        <Routes>
          <Route path="/marketplace/landing" element={<MarketplaceLanding />} />
          <Route path="/marketplace/*" element={<MarketplaceLanding />} />
        </Routes>
      ) : isConsentCallbackRoute ? (
        /* Consent callback must be public — Azure redirects here after admin consent */
        <Routes>
          <Route path="/consent-callback" element={<ConsentCallback />} />
        </Routes>
      ) : (
          <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <AppBar
            position="fixed"
            sx={{
              width: { sm: `calc(100% - ${drawerWidth}px)` },
              ml: { sm: `${drawerWidth}px` },
            }}
          >
            <Toolbar>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2, display: { sm: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                {(() => {
                  const headerLogo = customerAssets.find(asset => asset.assetType === 'HeaderLogo')
                  console.log('App.tsx: Rendering AppBar - customerAssets:', customerAssets.length, 'headerLogo found:', !!headerLogo)
                  return headerLogo ? (
                    <img 
                      src={headerLogo.blobUrl} 
                      alt={customerData?.companyName || 'Logo'} 
                      style={{ 
                        height: '40px', 
                        maxWidth: '200px', 
                        objectFit: 'contain' 
                      }}
                      onError={(e) => {
                        console.error('Logo failed to load:', headerLogo.blobUrl)
                        e.currentTarget.style.display = 'none'
                      }}
                    />
                  ) : (
                    <Typography variant="h6" noWrap>
                      {customerData?.companyName || 'Loading...'}
                    </Typography>
                  )
                })()}
              </Box>
              {isAuthenticated ? (
                <>
                  <Typography variant="body2" sx={{ mr: 2 }}>
                    {customerData?.subscriptionTier} Plan
                  </Typography>
                  {!isSupportExcludedPlan && (
                    <Tooltip title="Contact Support">
                      <IconButton color="inherit" onClick={handleOpenSupport}>
                        <SupportAgentIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <>
                    <IconButton
                      onClick={handleMenuOpen}
                      size="small"
                      sx={{ ml: 2 }}
                      aria-controls={anchorEl ? 'account-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={anchorEl ? 'true' : undefined}
                    >
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                        {accounts[0]?.name?.charAt(0) || 'U'}
                      </Avatar>
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      id="account-menu"
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                      onClick={handleMenuClose}
                      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    >
                      <MenuItem disabled>
                        <Typography variant="body2">{accounts[0]?.name}</Typography>
                      </MenuItem>
                      <MenuItem disabled>
                        <Typography variant="caption" color="text.secondary">
                          {accounts[0]?.username}
                        </Typography>
                      </MenuItem>
                      <Divider />
                      <MenuItem onClick={handleLogout}>
                        <ListItemIcon>
                          <LogoutIcon fontSize="small" />
                        </ListItemIcon>
                        Logout
                      </MenuItem>
                    </Menu>
                  </>
                </>
              ) : (
                <Button color="inherit" onClick={handleLogin}>
                  Login
                </Button>
              )}
            </Toolbar>
          </AppBar>
          <Box
            component="nav"
            sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
          >
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{
                keepMounted: true,
              }}
              sx={{
                display: { xs: 'block', sm: 'none' },
                '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
              }}
            >
              {drawer}
            </Drawer>
            <Drawer
              variant="permanent"
              sx={{
                display: { xs: 'none', sm: 'block' },
                '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
              }}
              open
            >
              {drawer}
            </Drawer>
          </Box>
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              p: 3,
              width: { sm: `calc(100% - ${drawerWidth}px)` },
            }}
          >
            <Toolbar />
            <Container maxWidth="xl">
              {!isAuthenticated ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '60vh',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h4" gutterBottom>
                    Welcome to TC Configuration Portal
                  </Typography>
                  <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 4 }}>
                    Please sign in to access your security policy management dashboard
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleLogin}
                    startIcon={<AccountIcon />}
                  >
                    Sign In
                  </Button>
                </Box>
              ) : !customerData || checkingSetupStatus || checkingCertificateStatus ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '60vh',
                    gap: 2,
                  }}
                >
                  <CircularProgress size={60} />
                  <Typography variant="body1" color="text.secondary">
                    {checkingSetupStatus
                      ? 'Checking setup status...'
                      : checkingCertificateStatus
                        ? 'Checking certificate status...'
                        : 'Loading customer data...'}
                  </Typography>
                </Box>
              ) : needsAdminSetup ? (
                <AdminSetupFlow 
                  tenantId={customerData.tenantId} 
                  tenantDomain={customerData.userEmail?.split('@')[1] || 'contoso.com'}
                />
              ) : isPortalGated ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 3 }}>
                  <LockIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                  <Typography variant="h5">Subscription Required</Typography>
                  <Typography variant="body1" color="text.secondary" textAlign="center">
                    An active TC-Config subscription is required to access this portal.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button variant="outlined" onClick={() => window.open(SOFTWARE_URL, '_self')}>Close</Button>
                    <Button variant="contained" onClick={() => window.open(MARKETPLACE_URL, '_blank')}>Purchase</Button>
                  </Box>
                </Box>
              ) : (
                <>
                  {isLifecycleLocked && (
                    <Alert
                      severity={portalAccess.lifecycleState === 'Unsubscribed' ? 'error' : 'warning'}
                      sx={{ mb: 2, position: 'sticky', top: 64, zIndex: 1200 }}
                    >
                      {portalAccess.lifecycleState === 'Unsubscribed'
                        ? `Your subscription has been cancelled. Your data is retained in read-only mode until ${portalAccess.dataRetentionUntil ? new Date(portalAccess.dataRetentionUntil).toLocaleDateString() : 'pending'}. After this date all data will be permanently deleted.`
                        : 'Your subscription is currently suspended. Access is read-only until the billing issue is resolved in your Azure Marketplace account.'}
                    </Alert>
                  )}
                  {isEnterprisePortalLocked ? (
                <Routes>
                  <Route
                    path="/"
                    element={
                      <Box sx={{ maxWidth: 760, mx: 'auto', mt: 4 }}>
                        <Typography variant="h4" gutterBottom>
                          Enterprise Client License Required
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                          {portalAccess.portalLockReason || 'Your MSP must attach a valid Enterprise Client License before full policy deployment and group creation are available.'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Button variant="contained" component={Link} to="/certificate-wizard">
                            Open Certificate Wizard
                          </Button>
                          <Button variant="outlined" component={Link} to="/msp-access">
                            Open MSP Access
                          </Button>
                          <Button variant="outlined" component={Link} to="/deploy">
                            Branding-Only Deployment
                          </Button>
                        </Box>
                      </Box>
                    }
                  />
                  <Route path="/deploy" element={<DeploymentWizard tenantId={customerData?.tenantId} mspTenantId={verifiedMspTenantId} />} />
                  {isDevelopment && (
                    <Route
                      path="/migrations/*"
                      element={
                        <Suspense fallback={<Box display="flex" justifyContent="center" p={4}><MuiCircularProgress /></Box>}>
                          <CustomerMigrationsModule />
                        </Suspense>
                      }
                    />
                  )}
                  <Route path="/assets" element={<AssetManager tenantId={customerData?.tenantId} />} />
                  <Route path="/msp-access" element={<MSPAccess tenantId={customerData?.tenantId} />} />
                  <Route path="/account" element={<AccountSettings tenantId={customerData?.tenantId} />} />
                  <Route
                    path="/certificate-wizard"
                    element={renderCertificateWizardRoute()}
                  />
                  <Route path="/consent-callback" element={<ConsentCallback />} />
                  <Route path="/marketplace/landing" element={<MarketplaceLanding />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              ) : (
                <Routes>
                  <Route path="/" element={<PoliciesView tenantId={customerData?.tenantId} />} />
                  <Route path="/deploy" element={<DeploymentWizard tenantId={customerData?.tenantId} mspTenantId={verifiedMspTenantId} />} />
                  <Route path="/policies" element={<PoliciesView tenantId={customerData?.tenantId} />} />
                  {isDevelopment && (
                    <Route
                      path="/migrations/*"
                      element={
                        <Suspense fallback={<Box display="flex" justifyContent="center" p={4}><MuiCircularProgress /></Box>}>
                          <CustomerMigrationsModule />
                        </Suspense>
                      }
                    />
                  )}
                  <Route path="/ai-assistant" element={<AIAssistant tenantId={customerData?.tenantId} />} />
                  <Route path="/assets" element={<AssetManager tenantId={customerData?.tenantId} />} />
                  <Route path="/msp-access" element={<MSPAccess tenantId={customerData?.tenantId} />} />
                  <Route path="/account" element={<AccountSettings tenantId={customerData?.tenantId} />} />
                  <Route
                    path="/certificate-wizard"
                    element={renderCertificateWizardRoute()}
                  />
                  <Route path="/consent-callback" element={<ConsentCallback />} />
                  <Route path="/marketplace/landing" element={<MarketplaceLanding />} />
                </Routes>
                  )}
                  <SupportDialog
                    open={supportOpen}
                    onClose={handleCloseSupport}
                    onSubmit={handleSubmitSupport}
                    onEmailSupport={handleEmailSupport}
                    submitting={supportSubmitting}
                    error={supportError}
                    success={supportSuccess}
                    context={supportContext}
                  />
                </>
              )}
            </Container>
          </Box>
        </Box>
      )}
    </ThemeProvider>
  )
}

export default App

// Force redeploy - 12/09/2025 13:37:55
